import FormattedAddress from "components/Atoms/FormattedAddress";
import { PhosphorWarning } from "components/Atoms/PhosphorIcon";
import PhoneRow from "components/DetailPage/PhoneRow";
import { FormSection, InPageEditableSectionField } from "components/MainSectionTemplate/types";
import AreYouSureModal from "components/modals/AreYouSureModal";
import { ContactSidePanel } from "components/SidePanel/contact/ContactSidePanel";
import WebsiteLink from "components/Website/WebsiteLink";
import BirthdateRow from "contact/detail/components/BirthdateRow";
import ContactMergeModal from "contact/detail/merge/app";
import moment from "moment";
import React from "react";
import { Message } from "semantic-ui-react";
import { isFieldHidden } from "./utils";
import { newLineToBr } from "utilities";
import { DeleteDialog } from "components";
import { mapShowFormStateToUpdateShowInternalInstruction, mapShowToShowFormState } from "shared/utils/fieldsHelper/showFields";

export const checkVenueContactSection = (
  contact: server.dto.Contact,
  formBehaviour: server.dto.WebformFunctionGroupContactFormBehavior,
  show: server.dto.Show,
  numberOfSimilarContacts: number
): FormSection => ({
  key: "checkVenueContact",
  type: "regular",
  hidden: !contact,
  title: <div className="flex justify-space-between">
    {site.currentEnvironment.functionGroups.venue.singular} - {RESX.Contact.resxContact}
    {!contact?.isValidated && <PhosphorWarning iconSize="large" />}
  </div>,
  fields: contact ? [
    ...checkContactFields(contact, formBehaviour), {
      key: "save",
      label: "",
      render: () => <CheckContactComponent
        contact={contact}
        show={show}
        type="venue"
        numberOfSimilarContacts={numberOfSimilarContacts}
      />,
    }
  ] : []
});

export const checkPromoterContactSection = (
  contact: server.dto.Contact,
  formBehaviour: server.dto.WebformFunctionGroupContactFormBehavior,
  show: server.dto.Show,
  numberOfSimilarContacts: number
): FormSection => ({
  key: "checkPromoterContact",
  type: "regular",
  hidden: !contact,
  title: <div className="flex justify-space-between">
    {site.currentEnvironment.functionGroups.promoter.singular} - {RESX.Contact.resxContact}
    {!contact?.isValidated && <PhosphorWarning iconSize="large" />}
  </div>,
  fields: contact ? [
    ...checkContactFields(contact, formBehaviour), {
      key: "save",
      label: "",
      render: () => <CheckContactComponent
        contact={contact}
        show={show}
        type="promoter"
        numberOfSimilarContacts={numberOfSimilarContacts}
      />,
    }
  ] : []
});

const checkContactFields = (
  contact: server.dto.Contact,
  formBehaviour: server.dto.WebformFunctionGroupContactFormBehavior,
): Array<InPageEditableSectionField> => [{
  key: "name",
  label: RESX.GeneralLabels.Name,
  isEmpty: !contact.name.fullName,
  render: () => <ContactSidePanel
    contactId={contact.contactId}
    trigger={<a>{contact.name.fullName}</a>}
  />,
}, {
  key: "email",
  label: RESX.GeneralLabels.Email,
  isEmpty: !contact.emailAddresses.length,
  render: () => <div>
    {contact.emailAddresses.map((email, idx) =>
      <div key={`${email}-${idx}`}><a href={`mailto:${email}`}>{email}</a></div>)}
  </div>
}, {
  key: "mobileNumbers",
  label: RESX.Contact.Mobile,
  isEmpty: !contact.mobileNumbers.length,
  hidden: isFieldHidden(formBehaviour.mobile),
  render: () => <PhoneRow numbers={contact.mobileNumbers} />
}, {
  key: "facebook",
  label: "Facebook",
  isEmpty: !contact.website,
  hidden: isFieldHidden(formBehaviour.facebook),
  render: () => <WebsiteLink website={contact.facebook} />
}, {
  key: "address",
  label: RESX.Address.resxAddress,
  isEmpty: !contact.address,
  hidden: isFieldHidden(formBehaviour.address),
  render: () => <FormattedAddress address={contact.address} />
}, {
  key: "position",
  label: RESX.Contact.Position,
  isEmpty: !contact.position,
  hidden: isFieldHidden(formBehaviour.position),
  render: () => <span>{contact.position}</span>
}, {
  key: "birthDate",
  label: RESX.Contact.DateOfBirth,
  isEmpty: !contact.birthdate,
  hidden: isFieldHidden(formBehaviour.birthDate),
  render: () => <BirthdateRow birthdate={moment.utc(contact.birthdate)} />,
}, {
  key: "nationality",
  label: RESX.Passport.Nationality,
  isEmpty: !contact.nationality,
  hidden: isFieldHidden(formBehaviour.nationality),
  render: () => <span>{contact.nationality}</span>
}, {
  key: "passportNumber",
  label: RESX.Passport.PassportNumber,
  isEmpty: !contact.passport || !contact.passport.number,
  hidden: isFieldHidden(formBehaviour.passportNumber),
  render: () => <span>{contact.passport?.number}</span>
}, {
  key: "socialSecurity",
  label: RESX.Contact.SocialSecurity,
  isEmpty: !contact.socialSecurity,
  hidden: isFieldHidden(formBehaviour.socialSecurity),
  render: () => <span>{contact.socialSecurity}</span>
}, {
  key: "additionalInformation",
  label: RESX.Company.AdditionalInformation,
  isEmpty: !contact.additional,
  hidden: !formBehaviour.additional,
  render: () => <div>{newLineToBr(contact.additional)}</div>
}]

interface CheckContactComponentProps {
  contact: server.dto.Contact;
  show: server.dto.Show;
  numberOfSimilarContacts: number;
  type: "venue" | "promoter";
}

export const CheckContactComponent = ({
  contact, show, type, numberOfSimilarContacts
}: CheckContactComponentProps) => {
  const [isAdding, setIsAdding] = React.useState(false);
  const [isMerging, setIsMerging] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const removeContact = async () => {
    const instruction = mapShowFormStateToUpdateShowInternalInstruction(mapShowToShowFormState(show), show.tour);
    const updateRes = await window.CoreApi.Show.UpdateForInternalEnvironment({
      ...instruction,
      showId: show.showId,
      venueContactId: type === "venue" ? null : instruction.venueContactId,
      promoterContactId: type === "promoter" ? null : instruction.promoterContactId,
    });

    if (updateRes.data.hasErrors) {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, updateRes.data.errors[0].message, "tiny", "red");
      return;
    }

    if (!contact.isValidated && contact.permissions.canDelete) {
      await window.CoreApi.Contact.Delete(contact.contactId);
    }

    window.location.reload();
  }

  const canMerge = !contact.isValidated && contact.permissions.canMerge;
  const canRemoveContact = !contact.isValidated && show.permissions.canModify;

  const displayActions = canMerge || canRemoveContact;

  return <>
    {isDeleting && <DeleteDialog
      isOpen
      message={RESX.GeneralWarnings.DeleteSure}
      onDelete={removeContact}
      handleClose={() => setIsDeleting(false)}
      spinForever
    />}
    {isAdding && <AreYouSureModal
      title={RESX.Buttons.Add}
      yesButtonLabel={RESX.Buttons.Add}
      yesButtonColor="green"
      onClose={() => { setIsAdding(false); }}
      onYes={async () => window.CoreApi.Contact.Validate({ contactId: contact.contactId }).then(() => { window.location.reload(); })}
    />}
    <ContactMergeModal
      webformId={show.showId}
      isOpen={isMerging}
      contactId={contact.contactId}
      contactFullName={contact.name.fullName}
      onClose={() => { setIsMerging(false); }}
    />
    <div className="flex-column gap grow1">
      {(!contact.isValidated && !contact.permissions.canMerge) && <Message color="red">
        {RESX.Webform.YouHaveInsufficientAccessToContactSection}
      </Message>}

      {displayActions && <div>
        {canMerge && <>
          <div>
            <a onClick={() => setIsMerging(true)}>{RESX.Contact.MergeWithAContactPersonThatIsAlreadyInTheDatabase}</a>
            <div className="text-sublabel">{RESX.Contact.NumberOfContactPersonsFoundInDatabaseThatAreSimilarTo} {contact.name.fullName}: {numberOfSimilarContacts}</div>
          </div>
          <div>
            <a onClick={() => setIsAdding(true)}>{RESX.GeneralLabels.AddToDatabase}</a>
            <div className="text-sublabel">{RESX.Webform.IfThisContactPersonDoesntExists}</div>
          </div>
        </>}
        {canRemoveContact && <div>
          <a onClick={() => setIsDeleting(true)}>{RESX.Buttons.btnDelete}</a>
          <div className="text-sublabel">{RESX.Webform.RemoveInformationIfThisContactPersonShouldnt}</div>
        </div>}
      </div>}
    </div>
  </>
}